/* Make a <video> actually start on its own, on iOS included.
 *
 * `<video autoPlay muted playsInline>` is correct markup and it is not enough. React sets
 * `muted` as a DOM PROPERTY and never renders it as an ATTRIBUTE (facebook/react#6544, open
 * since 2016) — the built bundle carries it as `muted:!0`. WebKit decides whether a video is
 * allowed to start by itself from the element as it finds it when loading begins, and React
 * assigns `src` during element creation, which can start that load before the property lands.
 * The video then looks unmuted to the autoplay policy, is refused, and iOS falls back to the
 * poster with a play button drawn over it. That is the "images show up but don't animate"
 * symptom exactly, and no amount of getting the JSX attributes right fixes it, because the
 * JSX attributes were already right.
 *
 * So: write the real attribute, and ask for playback rather than waiting to be given it.
 *
 * The retries are because `preload="metadata"` means the first play() can be refused simply
 * for having nothing buffered yet — a different refusal from a policy one, and the one that
 * clears by itself.
 *
 * THERE USED TO BE EXACTLY TWO RETRIES, both `once`, and when a clip is one of several on a
 * page that is also loading its own content, two is not always enough: `loadeddata` and
 * `canplay` can both fire while the buffer is still too thin to start, and after that nothing
 * ever asked again. The card then sat on its poster forever with no way back. So there is a
 * third event — `canplaythrough`, which by definition has enough — and two timed attempts
 * after it, which cover the case where every event has already fired before this ref ran.
 * Bounded on purpose: a browser refusing on POLICY will refuse every time, and the poster is
 * the right answer for that. This only rescues the refusals that clear on their own.
 *
 * A rejection is swallowed on purpose. If a browser still refuses, the poster is a perfectly
 * good outcome and an unhandled rejection in the console is not.
 */
export const autoplayVideo = (el) => {
  if (!el) return; // ref callbacks fire with null on unmount

  // The attribute is the one the autoplay policy reads; the properties keep the element
  // consistent for anything that inspects it later.
  el.setAttribute("muted", "");
  el.muted = true;
  el.defaultMuted = true;
  el.playsInline = true;

  const timers = [];
  const stop = () => { timers.forEach(clearTimeout); timers.length = 0; };

  const attempt = () => {
    if (!el.paused) { stop(); return; }   // already running; nothing to ask for
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  };

  attempt();
  el.addEventListener("loadeddata", attempt, { once: true });
  el.addEventListener("canplay", attempt, { once: true });
  el.addEventListener("canplaythrough", attempt, { once: true });
  // The events above may all have fired before this ref ran — a cached clip is ready
  // immediately — so two plain retries cover the window they cannot.
  timers.push(setTimeout(attempt, 600), setTimeout(attempt, 1800));
  el.addEventListener("playing", stop, { once: true });
};
